import React, { memo, useMemo, useRef, useEffect, useState, useCallback } from 'react'
import { NodeProps, useReactFlow } from '@xyflow/react'
import { Button } from '@/components/ui/button'
import { Filter, RefreshCw, Settings } from 'lucide-react'
import NodeBaseContainer from '@/components/nodes/base/base-container/NodeBaseContainer'
import SmartNotesTabs from '@/components/nodes/smartnotes/components/SmartNotesTabs'
import NodeReferenceSelectorDialog from '@/components/dialogs/reference-selector-dialog/NodeReferenceSelectorDialog'
import NodeDataSettingsDialog from '@/components/dialogs/data-settings-dialog/NodeDataSettingsDialog'
import NodeConfigurationDialog from '@/components/dialogs/configuration-dialog/NodeConfigurationDialog'
import useDataSettings from '@/hooks/useDataSettings'
import type { SmartNotesNodeData } from './types'
import {
  useSmartNotesProcessing,
  useSmartNotesReferences,
} from './hooks'
import { PROCESSING_TIMEOUT_MS } from './types'

// Helper function for scroll handling (inline implementation)
const handleScroll = (container: HTMLElement, deltaY: number): boolean => {
  const { scrollTop, scrollHeight, clientHeight } = container
  const isAtTop = scrollTop === 0
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

  if ((deltaY < 0 && isAtTop) || (deltaY > 0 && isAtBottom)) {
    return false // Don't prevent default, allow parent scrolling
  }

  container.scrollTop += deltaY
  return true // Prevent default to stop propagation
}

const SmartNotesNode: React.FC<NodeProps & { data: SmartNotesNodeData }> = ({
  id,
  data,
  selected,
}) => {
  const { updateNodeData } = useReactFlow()

  // Direct state management like working nodes
  const [localContent, setLocalContent] = useState(data.content || '')
  const [extractLinks, setExtractLinks] = useState(!!data.extractLinks)
  const [extractAdultContent, setExtractAdultContent] = useState(!!data.extractAdultContent)
  const [extractDepthLevel, setExtractDepthLevel] = useState(data.extractDepthLevel || 2)
  const [isConfigDialogVisible, setIsConfigDialogVisible] = useState(false)
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] = useState(false)
  const [nodeConfig, setNodeConfig] = useState(data.configuration || {})

  // Sync local state from data prop
  useEffect(() => {
    setLocalContent(data.content || '')
  }, [data.content])

  useEffect(() => {
    setExtractLinks(!!data.extractLinks)
  }, [data.extractLinks])

  useEffect(() => {
    setExtractAdultContent(!!data.extractAdultContent)
  }, [data.extractAdultContent])

  useEffect(() => {
    setExtractDepthLevel(data.extractDepthLevel || 2)
  }, [data.extractDepthLevel])

  useEffect(() => {
    setNodeConfig(data.configuration || {})
  }, [data.configuration])

  // Direct syncNodeData like working nodes
  const syncNodeData = useCallback((updates: Partial<SmartNotesNodeData>) => {
    updateNodeData(id, { ...data, ...updates })
  }, [id, data, updateNodeData])

  // Get processing and reference hooks
  const { handleAnalyze, handleCreateDocument, createCooldowns, urls } = useSmartNotesProcessing({
    id,
    data,
    syncNodeData,
  })
  const {
    isReferenceDialogVisible,
    setIsReferenceDialogVisible,
    availableNodes,
    handleSaveReferences,
  } = useSmartNotesReferences({
    id,
    data,
    syncNodeData
  })

  // Data settings hook
  const {
    dataSettings,
    handleDataSettingsChange,
    handleRemoveInputLink,
    handleSyncInputLink,
    handleOpenDataSettingsDialog,
  } = useDataSettings(
    id,
    data,
    syncNodeData,
    false,
    '',
    new Map(),
    '',
    setIsDataSettingsDialogVisible
  )

  // Handle opening reference dialog
  const handleOpenReferenceDialog = useCallback(() => {
    setIsReferenceDialogVisible(true)
  }, [setIsReferenceDialogVisible])

  // Refresh linked knowledge
  const handleRefreshLinkedKnowledge = useCallback(() => {
    const smartNotesData = data as SmartNotesNodeData
    handleSaveReferences(smartNotesData.setReferences || {}, true)
  }, [data, handleSaveReferences])

  // Custom buttons for NodeBaseContainer menu - memoized to prevent re-renders
  const customButtons = useMemo(() => [
    {
      id: 'data-settings',
      onClick: handleOpenDataSettingsDialog,
      icon: <Filter size={16} />,
      tooltip: 'Data connection settings',
    },
    {
      id: 'refresh-linked-knowledge',
      onClick: handleRefreshLinkedKnowledge,
      icon: <RefreshCw size={16} />,
      tooltip: 'Refresh imported data',
    },
    {
      id: 'configuration',
      onClick: () => setIsConfigDialogVisible(true),
      icon: <Settings size={16} />,
      tooltip: 'Configuration',
    },
  ], [handleOpenDataSettingsDialog, handleRefreshLinkedKnowledge, setIsConfigDialogVisible])

  // Derived state
  const statusMessage = data.statusMessage || ''
  const recommendations = data.recommendations || []
  const isMinimized = data.isMinimized || false
  const isLocked = data.isLocked || false

  // Debug logging for recommendations
  useEffect(() => {
    console.log('SmartNotes NodeSmartNotes: Current recommendations:', recommendations);
    console.log('SmartNotes NodeSmartNotes: Current statusMessage:', statusMessage);
    console.log('SmartNotes NodeSmartNotes: Current documentRequest:', data.documentRequest);
  }, [recommendations, statusMessage, data.documentRequest]);

  // Ref for recommendations scroll container
  const recommendationsScrollRef = useRef<HTMLDivElement>(null)

  // Attach wheel event for recommendations scroll container
  useEffect(() => {
    const container = recommendationsScrollRef.current
    if (!container) return

    const handleWheelEvent = (e: WheelEvent) => {
      if (handleScroll(container, e.deltaY)) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    container.addEventListener('wheel', handleWheelEvent, { passive: false })
    return () => {
      container.removeEventListener('wheel', handleWheelEvent)
    }
  }, [recommendations, recommendations.length])

  // Auto-unlock processing if stuck for too long
  useEffect(() => {
    const smartNotesData = data as SmartNotesNodeData
    if (smartNotesData.documentRequest?.processing) {
      const startedAt = smartNotesData.documentRequest?.processingStartedAt || Date.now()
      if (!smartNotesData.documentRequest.processingStartedAt) {
        syncNodeData({
          documentRequest: {
            ...smartNotesData.documentRequest,
            processingStartedAt: Date.now(),
          },
        })
      }

      const now = Date.now()
      const elapsed = now - startedAt
      const remaining = Math.max(PROCESSING_TIMEOUT_MS - elapsed, 1000)
      const timeout = setTimeout(() => {
        if (smartNotesData.documentRequest?.processing) {
          syncNodeData({
            documentRequest: {
              ...smartNotesData.documentRequest,
              processing: false,
              processingStartedAt: undefined,
              'sysagent-cmd': '',
              'sysagent-response': 'SmartNotesNode-Analysis-Timeout',
            },
            statusMessage: 'Processing timed out. Please try again.',
          })
        }
      }, remaining)
      return () => clearTimeout(timeout)
    }
  }, [(data as SmartNotesNodeData).documentRequest?.processing, syncNodeData])

  // Compute status for NodeBaseContainer
  const nodeStatus = useMemo(() => {
    const smartNotesData = data as SmartNotesNodeData
    if (smartNotesData.documentRequest?.processing) return 'loading'
    if (statusMessage && statusMessage.toLowerCase().includes('error'))
      return 'error'
    if (recommendations && recommendations.length > 0) return 'success'
    return 'initial'
  }, [(data as SmartNotesNodeData).documentRequest?.processing, statusMessage, recommendations])

  return (
    <>
      <NodeBaseContainer
        id={id}
        data={data}
        resizable={!isLocked}
        assignedEmails={
          Array.isArray(data.assignedEmails)
            ? data.assignedEmails
            : data.assignedEmails
              ? data.assignedEmails.trim().split(/\s+/).filter(Boolean)
              : []
        }
        nodeTitle={data.label || 'Smart Notes'}
        customButtons={customButtons}
        isProcessing={!!data.documentRequest?.processing}
        status={nodeStatus}
        onAssignUsersClick={() => {
          const emailsStr = window.prompt(
            'Enter emails separated by space',
            Array.isArray(data.assignedEmails)
              ? data.assignedEmails.join(' ')
              : data.assignedEmails || ''
          )
          if (emailsStr !== null) {
            const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
            syncNodeData({ assignedEmails: emailsArr })
          }
        }}
        selected={selected}
      >
        {!isMinimized && (
          <>
            {/* Function bar */}
            <div className="flex gap-2 mb-2 mt-2 px-2 flex-shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={handleOpenReferenceDialog}
              >
                Set Data Link(s)
              </Button>
            </div>

            {/* Main content area */}
            <div
              className="flex flex-col px-2 pb-2"
              style={{ height: 'calc(100% - 60px)' }}
            >
              {/* Top Section: Tabs and Detected Links */}
              <div
                className="flex flex-col"
                style={{ height: '50%', minHeight: '200px' }}
              >
                <SmartNotesTabs
                  content={localContent}
                  setContent={setLocalContent}
                  syncNodeData={syncNodeData}
                  urls={urls}
                  handleAnalyze={handleAnalyze}
                  isLocked={isLocked || !!data.documentRequest?.processing}
                  handleCreateDocument={handleCreateDocument}
                  data={data}
                  linkedKnowledge={data.linkedKnowledge}
                  onLinkedKnowledgeChange={(lk) =>
                    syncNodeData({ linkedKnowledge: lk })
                  }
                  hideLinksAndAnalyze
                />

                {/* Detected links */}
                {urls.length > 0 && (
                  <div className="w-full mt-2 flex-shrink-0">
                    <label className="flex items-center gap-2 font-semibold text-xs text-gray-500 mb-1">
                      <input
                        type="checkbox"
                        checked={extractLinks}
                        onChange={(e) => {
                          const newValue = e.target.checked
                          setExtractLinks(newValue)
                          syncNodeData({ extractLinks: newValue })
                        }}
                        style={{ accentColor: '#2563eb' }}
                      />
                      Extract content from next web addresses when you start
                      Analyze & Recommend process
                      {/* Adult material and Depth level options */}
                      <span className="flex items-center gap-2 ml-4">
                        <input
                          type="checkbox"
                          checked={extractAdultContent}
                          onChange={(e) => {
                            const newValue = e.target.checked
                            setExtractAdultContent(newValue)
                            syncNodeData({ extractAdultContent: newValue })
                          }}
                          className="nodrag nowheel"
                        />
                        <span className="text-xs">Adult material</span>
                        {extractAdultContent && (
                          <span className="flex items-center gap-1 ml-2">
                            <label className="text-xs">Depth level:</label>
                            <input
                              type="number"
                              min={1}
                              max={10}
                              value={extractDepthLevel}
                              onChange={(e) => {
                                const val = Number(e.target.value)
                                setExtractDepthLevel(val)
                                syncNodeData({ extractDepthLevel: val })
                              }}
                              className="w-14 border rounded p-1 nodrag nowheel"
                              style={{ fontSize: '12px' }}
                            />
                          </span>
                        )}
                      </span>
                    </label>
                    <p className="text-sm break-all">
                      {urls.map((url, idx) => (
                        <React.Fragment key={url + idx}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                          >
                            {url}
                          </a>
                          {idx < urls.length - 1 && ', '}
                        </React.Fragment>
                      ))}
                    </p>
                  </div>
                )}
              </div>

              {/* Middle: Analyze & Recommend Button */}
              <div className="flex justify-center items-center py-3 flex-shrink-0">
                <Button
                  className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  onClick={handleAnalyze}
                  disabled={isLocked || !!data.documentRequest?.processing}
                >
                  Analyze & Recommend
                </Button>
              </div>

              {/* Bottom Section: Analysis Results and Recommendations */}
              <div
                className="flex flex-col"
                style={{ height: 'calc(50% - 60px)', minHeight: '100px' }}
              >
                {statusMessage && (
                  <div className="text-sm text-gray-600 mb-2 flex-shrink-0">
                    {statusMessage}
                  </div>
                )}

                {/* Intent Analysis Display */}
                {data.intentAnalysis && (
                  <div className="mb-3 p-2 bg-blue-50 rounded-md border border-blue-200 flex-shrink-0">
                    <h4 className="font-semibold text-blue-800 text-sm mb-1">Intent Analysis</h4>
                    <div className="text-xs text-blue-700">
                      <div className="mb-1">
                        <span className="font-medium">Primary:</span> {data.intentAnalysis.primaryIntent.description}
                        <span className="ml-2 text-blue-600">({data.intentAnalysis.primaryIntent.confidence}% confidence)</span>
                      </div>
                      <div className="flex gap-4 text-xs">
                        <span><span className="font-medium">Domain:</span> {data.intentAnalysis.domain}</span>
                        <span><span className="font-medium">Complexity:</span> {data.intentAnalysis.complexity}</span>
                        <span><span className="font-medium">Urgency:</span> {data.intentAnalysis.urgency}</span>
                        <span><span className="font-medium">Completeness:</span> {data.intentAnalysis.completeness}%</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Have and Need Analysis Display */}
                {data.haveAndNeedAnalysis && (
                  <div className="mb-3 p-2 bg-green-50 rounded-md border border-green-200 flex-shrink-0">
                    <h4 className="font-semibold text-green-800 text-sm mb-1">Have & Need Analysis</h4>
                    <div className="text-xs text-green-700 grid grid-cols-2 gap-2">
                      <div>
                        <div className="font-medium text-green-800 mb-1">What You Have:</div>
                        <ul className="list-disc list-inside text-xs space-y-0.5">
                          {data.haveAndNeedAnalysis.have.strengths.slice(0, 2).map((strength, idx) => (
                            <li key={idx}>{strength}</li>
                          ))}
                        </ul>
                        <div className="mt-1 text-xs">
                          <span className="font-medium">Clarity:</span> {data.haveAndNeedAnalysis.have.clarity}%
                        </div>
                      </div>
                      <div>
                        <div className="font-medium text-green-800 mb-1">What You Need:</div>
                        <ul className="list-disc list-inside text-xs space-y-0.5">
                          {data.haveAndNeedAnalysis.need.missingInformation.slice(0, 2).map((missing, idx) => (
                            <li key={idx}>{missing}</li>
                          ))}
                        </ul>
                        <div className="mt-1 text-xs">
                          <span className="font-medium">Priority:</span> {data.haveAndNeedAnalysis.need.priority}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* DIKW Model Display */}
                {(data.dataExtraction || data.informationExtraction || data.knowledgeExtraction || data.wisdomExtraction) && (
                  <div className="mb-3 p-2 bg-purple-50 rounded-md border border-purple-200 flex-shrink-0">
                    <h4 className="font-semibold text-purple-800 text-sm mb-2">DIKW Analysis</h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {/* Data Extraction */}
                      {data.dataExtraction && (
                        <div className="bg-red-50 p-2 rounded border border-red-200">
                          <div className="font-medium text-red-800 mb-1">Data</div>
                          <div className="text-red-700">
                            {data.dataExtraction.rawFacts.length > 0 && (
                              <div className="mb-1">
                                <span className="font-medium">Facts:</span> {data.dataExtraction.rawFacts.slice(0, 2).join(', ')}
                                {data.dataExtraction.rawFacts.length > 2 && '...'}
                              </div>
                            )}
                            {data.dataExtraction.numbers.length > 0 && (
                              <div className="mb-1">
                                <span className="font-medium">Numbers:</span> {data.dataExtraction.numbers.slice(0, 3).join(', ')}
                                {data.dataExtraction.numbers.length > 3 && '...'}
                              </div>
                            )}
                            <div className="text-xs mt-1">
                              <span className="font-medium">Quality:</span> {data.dataExtraction.metadata.dataQuality}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Information Extraction */}
                      {data.informationExtraction && (
                        <div className="bg-orange-50 p-2 rounded border border-orange-200">
                          <div className="font-medium text-orange-800 mb-1">Information</div>
                          <div className="text-orange-700">
                            {data.informationExtraction.patterns.length > 0 && (
                              <div className="mb-1">
                                <span className="font-medium">Patterns:</span> {data.informationExtraction.patterns.slice(0, 2).join(', ')}
                                {data.informationExtraction.patterns.length > 2 && '...'}
                              </div>
                            )}
                            {data.informationExtraction.relationships.length > 0 && (
                              <div className="mb-1">
                                <span className="font-medium">Relations:</span> {data.informationExtraction.relationships.slice(0, 2).join(', ')}
                                {data.informationExtraction.relationships.length > 2 && '...'}
                              </div>
                            )}
                            <div className="text-xs mt-1">
                              <span className="font-medium">Clarity:</span> {data.informationExtraction.metadata.clarity}%
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Knowledge Extraction */}
                      {data.knowledgeExtraction && (
                        <div className="bg-teal-50 p-2 rounded border border-teal-200">
                          <div className="font-medium text-teal-800 mb-1">Knowledge</div>
                          <div className="text-teal-700">
                            {data.knowledgeExtraction.insights.length > 0 && (
                              <div className="mb-1">
                                <span className="font-medium">Insights:</span> {data.knowledgeExtraction.insights.slice(0, 2).join(', ')}
                                {data.knowledgeExtraction.insights.length > 2 && '...'}
                              </div>
                            )}
                            {data.knowledgeExtraction.bestPractices.length > 0 && (
                              <div className="mb-1">
                                <span className="font-medium">Best Practices:</span> {data.knowledgeExtraction.bestPractices.slice(0, 2).join(', ')}
                                {data.knowledgeExtraction.bestPractices.length > 2 && '...'}
                              </div>
                            )}
                            <div className="text-xs mt-1">
                              <span className="font-medium">Depth:</span> {data.knowledgeExtraction.metadata.knowledgeDepth}%
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Wisdom Extraction */}
                      {data.wisdomExtraction && (
                        <div className="bg-indigo-50 p-2 rounded border border-indigo-200">
                          <div className="font-medium text-indigo-800 mb-1">Wisdom</div>
                          <div className="text-indigo-700">
                            {data.wisdomExtraction.strategicInsights.length > 0 && (
                              <div className="mb-1">
                                <span className="font-medium">Strategic:</span> {data.wisdomExtraction.strategicInsights.slice(0, 2).join(', ')}
                                {data.wisdomExtraction.strategicInsights.length > 2 && '...'}
                              </div>
                            )}
                            {data.wisdomExtraction.judgments.length > 0 && (
                              <div className="mb-1">
                                <span className="font-medium">Judgments:</span> {data.wisdomExtraction.judgments.slice(0, 2).join(', ')}
                                {data.wisdomExtraction.judgments.length > 2 && '...'}
                              </div>
                            )}
                            <div className="text-xs mt-1">
                              <span className="font-medium">Level:</span> {data.wisdomExtraction.metadata.wisdomLevel}%
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {recommendations && recommendations.length > 0 && (
                  <div
                    className="overflow-auto w-full flex-grow"
                    ref={recommendationsScrollRef}
                  >
                    <h3 className="font-semibold mb-2">Recommendations:</h3>
                    {(() => {
                      // Group recommendations by their group field
                      const refinableContent = recommendations.filter((rec: any) =>
                        typeof rec === 'object' && rec.group === 'refinable-content'
                      );
                      const templateRecommendations = recommendations.filter((rec: any) =>
                        typeof rec === 'object' && rec.group === 'template-recommendations'
                      );
                      const ungrouped = recommendations.filter((rec: any) =>
                        typeof rec === 'string' || !rec.group
                      );

                      console.log('SmartNotes grouping:', {
                        total: recommendations.length,
                        refinableContent: refinableContent.length,
                        templateRecommendations: templateRecommendations.length,
                        ungrouped: ungrouped.length,
                        rawRecommendations: recommendations
                      });

                      return (
                        <div className="space-y-3">
                          {/* Refinable Content Group */}
                          {refinableContent.length > 0 && (
                            <div>
                              <h4 className="font-medium text-green-700 text-sm mb-2 flex items-center">
                                <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                                Refinable Content ({refinableContent.length})
                              </h4>
                              <ul className="list-none space-y-2">
                                {refinableContent.map((recommendation, index) => {
                                  const rec = recommendation as any;
                                  const showCreate = rec.action === 'create-new' || rec.action === 'use-existing';
                                  let buttonLabel = 'Create';
                                  if (rec.action === 'use-existing') {
                                    buttonLabel = 'Create from Template';
                                  } else if (rec.action === 'create-new') {
                                    buttonLabel = 'Create New Custom Template';
                                  }
                                  return (
                                    <li key={`refinable-${index}`} className="text-sm bg-green-50 p-2 rounded border border-green-200">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="font-semibold text-green-800">{rec.title}</span>
                                        <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">
                                          Score: {rec.score}
                                        </span>
                                        {showCreate && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleCreateDocument(recommendation, index)}
                                            disabled={!!createCooldowns[index] || !!data.documentRequest?.processing}
                                            className="ml-auto"
                                          >
                                            {createCooldowns[index] ? 'Please wait...' : buttonLabel}
                                          </Button>
                                        )}
                                      </div>
                                      {rec.description && (
                                        <div className="text-xs text-green-700 mb-1">{rec.description}</div>
                                      )}
                                      {rec.why && (
                                        <div className="text-xs text-green-600">Why: {rec.why}</div>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}

                          {/* Template Recommendations Group */}
                          {templateRecommendations.length > 0 && (
                            <div>
                              <h4 className="font-medium text-blue-700 text-sm mb-2 flex items-center">
                                <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                                Template Recommendations ({templateRecommendations.length})
                              </h4>
                              <ul className="list-none space-y-2">
                                {templateRecommendations.map((recommendation, index) => {
                                  const rec = recommendation as any;
                                  const showCreate = rec.action === 'create-new' || rec.action === 'use-existing';
                                  let buttonLabel = 'Create';
                                  if (rec.action === 'use-existing') {
                                    buttonLabel = 'Create from Template';
                                  } else if (rec.action === 'create-new') {
                                    buttonLabel = 'Create New Custom Template';
                                  }
                                  return (
                                    <li key={`template-${index}`} className="text-sm bg-blue-50 p-2 rounded border border-blue-200">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="font-semibold text-blue-800">{rec.title}</span>
                                        <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">
                                          Score: {rec.score}
                                        </span>
                                        {showCreate && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleCreateDocument(recommendation, refinableContent.length + index)}
                                            disabled={!!createCooldowns[refinableContent.length + index] || !!data.documentRequest?.processing}
                                            className="ml-auto"
                                          >
                                            {createCooldowns[refinableContent.length + index] ? 'Please wait...' : buttonLabel}
                                          </Button>
                                        )}
                                      </div>
                                      {rec.description && (
                                        <div className="text-xs text-blue-700 mb-1">{rec.description}</div>
                                      )}
                                      {rec.guidance && (
                                        <div className="text-xs text-blue-600 mb-1">
                                          <strong>Guidance:</strong> {rec.guidance}
                                        </div>
                                      )}
                                      {rec.requiredInputs && rec.requiredInputs.length > 0 && (
                                        <div className="text-xs text-blue-600">
                                          <strong>Required:</strong> {rec.requiredInputs.join(', ')}
                                        </div>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}

                          {/* Ungrouped/Legacy Recommendations */}
                          {ungrouped.length > 0 && (
                            <div>
                              <h4 className="font-medium text-gray-700 text-sm mb-2">
                                Other Recommendations ({ungrouped.length})
                              </h4>
                              <ul className="list-disc pl-5 space-y-2">
                                {ungrouped.map((recommendation, index) => {
                                  const isString = typeof recommendation === 'string';
                                  const rec = isString
                                    ? {
                                      title: recommendation,
                                      action: recommendation.includes('Create a new') ? 'create-new' : '',
                                      why: '',
                                      purpose: '',
                                      description: '',
                                      score: undefined as number | undefined,
                                    }
                                    : recommendation;
                                  const showCreate = rec.action === 'create-new' || rec.action === 'use-existing';
                                  let buttonLabel = 'Create';
                                  if (rec.action === 'use-existing') {
                                    buttonLabel = 'Create from Template';
                                  } else if (rec.action === 'create-new') {
                                    buttonLabel = 'Create New Custom Template';
                                  }
                                  return (
                                    <li key={`ungrouped-${index}`} className="text-sm text-gray-700 flex flex-col gap-1 mb-2">
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold">{rec.title}</span>
                                        {showCreate && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleCreateDocument(recommendation, refinableContent.length + templateRecommendations.length + index)}
                                            disabled={!!createCooldowns[refinableContent.length + templateRecommendations.length + index] || !!data.documentRequest?.processing}
                                          >
                                            {createCooldowns[refinableContent.length + templateRecommendations.length + index] ? 'Please wait...' : buttonLabel}
                                          </Button>
                                        )}
                                      </div>
                                      {rec.why && (
                                        <div className="text-xs text-gray-500">Why: {rec.why}</div>
                                      )}
                                      {rec.description && (
                                        <div className="text-xs text-gray-400">{rec.description}</div>
                                      )}
                                      {typeof rec.score === 'number' && (
                                        <span className="text-xs text-gray-400">Score: {rec.score}</span>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
                {(!recommendations || recommendations.length === 0) && (
                  <div className="flex-grow min-h-0"></div>
                )}
              </div>
            </div>
          </>
        )}
      </NodeBaseContainer>

      {/* Set References Dialog */}
      {isReferenceDialogVisible && (
        <NodeReferenceSelectorDialog
          visible={isReferenceDialogVisible}
          onHide={() => setIsReferenceDialogVisible(false)}
          availableNodes={availableNodes}
          onSave={handleSaveReferences}
          initialSelected={(data as SmartNotesNodeData).setReferences}
        />
      )}

      {/* Data Settings Dialog */}
      {isDataSettingsDialogVisible && (
        <NodeDataSettingsDialog
          visible={isDataSettingsDialogVisible}
          onHide={() => setIsDataSettingsDialogVisible(false)}
          settings={dataSettings}
          nodeId={id}
          onSettingsChange={handleDataSettingsChange}
          onRemoveInputLink={handleRemoveInputLink}
          onSyncInputLink={handleSyncInputLink}
          forceShowAvailableNodes={true}
          debug={false}
        />
      )}

      {/* Configuration Dialog */}
      {isConfigDialogVisible && (
        <NodeConfigurationDialog
          visible={isConfigDialogVisible}
          onHide={() => setIsConfigDialogVisible(false)}
          configuration={nodeConfig as any}
          onSave={(config) => {
            setNodeConfig(config)
            syncNodeData({ configuration: config })
            setIsConfigDialogVisible(false)
          }}
        />
      )}
    </>
  )
}

export default memo(SmartNotesNode)
